<script setup lang="ts">
import vue, { onMounted, ref, watch, watchEffect } from 'vue';
import router from '@/router';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { arrowForwardOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import hexRgb from 'hex-rgb';
import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import rgbHex from 'rgb-hex';
import TextInput from '@/shared/components/sign-up-forms/TextInput.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useCustomForm from '@/shared/composables/useCustomForm';
import useProfileRefunds from '@/modules/profile/composables/useProfileRefunds';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import { useRoute } from 'vue-router';
import useTenant from '@/shared/composables/useTenant';
import CurrencyInput from '@/shared/components/sign-up-forms/CurrencyInput.vue';
import BackButton from '@/shared/components/BackButton.vue';

// Init
const { t } = useI18n();
const route = useRoute();
const walletId = ref(route.params.wallet_id?.toString());
const refundDetails: any = ref([]);
const { getRefundInfo, getWalletCurrentBalance, getWallet } = useWallet();
const { userWallet } = getWalletCurrentBalance(walletId.value, false, true);
const { refundInfo } = getRefundInfo(walletId.value);
const { wallet } = getWallet(walletId);
const { amount, iban, schema } = useProfileRefunds(refundInfo);
const nonRefundableValue = ref(0);
const { calculateShade } = useUtils();
const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString(), 0.2));
const amountErrorMessage = ref('');
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();

const { exchange } = useUtils();

// useCustomForm
const { isSuccess, isFormValid, onSubmit, onInputChange } = useCustomForm(schema, `/wallet/${walletId.value}/refund-request`, 'post', 'wallet.refund.requested', 'wallet.refund.requested_error', 5000);

const onAmountInputChange = (formValid: vue.ComputedRef<boolean>, fieldName: any, event: CustomEvent) => {
	if (event.type !== 'ion-blur') {
		amount.value = event.detail.value;
	}
};

// Watch refund success
watch(
	isSuccess,
	() => {
		if (isSuccess.value) {
			router.push(`/wallet/refunds/${walletId.value}`);
		}
	},
	{ immediate: true },
);

watchEffect(() => {
	if (userWallet.value && refundInfo.value) {
		// Reser data
		refundDetails.value = [];

		// Adding balance
		refundDetails.value.push({ title: t('wallet.refund.balance'), totalPrice: +userWallet.value.current_balance });
		if (amount.value === 0) {
			amount.value = +refundInfo.value.total_amount;
		}

		// Adding non refundable info
		if (+userWallet.value.current_balance - refundInfo.value.total_amount > 0) {
			nonRefundableValue.value = +userWallet.value.current_balance - refundInfo.value.total_amount;

			refundDetails.value.push({
				title: t('wallet.refund.not_refundable'),
				totalPrice: +userWallet.value.current_balance - refundInfo.value.total_amount,
			});

			if (!refundInfo.value.manual_refund_enabled) {
				refundDetails.value.push({ title: `- ${t('wallet.refund.not_refundable_items.onsite')}` });
			}
			refundDetails.value.push({ title: `- ${t('wallet.refund.not_refundable_items.vouchers')}` });
			refundDetails.value.push({ title: `- ${t('wallet.refund.not_refundable_items.discounts')}` });
			refundDetails.value.push({ title: `- ${t('wallet.refund.not_refundable_items.warranty')}` });
			if (!refundInfo.value.online_cutoff_days) {
				refundDetails.value.push({
					title: t('wallet.refund.not_refundable_items.mollie', {
						online_cutoff_days: refundInfo.value.online_cutoff_days,
					}),
				});
			}
		}

		// Adding more details
		refundDetails.value.push(
			...[
				{ title: t('wallet.refund.amount'), totalPrice: +amount.value },
				{
					title: t('wallet.refund.fee_incl_vat'),
					totalPrice: -(refundInfo.value.cost || 0),
					currency: '&euro;',
				},
			],
		);
	}
});

const { getRefundSchedule } = useWallet();
const { refundSchedule, isFetchedRefundScheduleConfig } = getRefundSchedule();

watchEffect(() => {
	if (isFetchedRefundScheduleConfig.value && (refundSchedule.value === undefined || refundSchedule.value?.refunds_enabled === false)) {
		router.push('/dashboard');
	}
});
</script>

<template>
	<ion-content :style="`background: linear-gradient(${primary_color}, ${primaryShade});`">
		<div class="flex flex-col justify-between h-full">
			<div class="px-[34px]">
				<form id="refundsRequestForm" class="flex flex-col" :validation-schema="schema" @submit="onSubmit">
					<TopBar>
						<template #left>
							<BackButton to="/dashboard" />
						</template>

						<template #center>
							<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
								{{ t('wallet.refund.request.title') }}
							</div>
						</template>
					</TopBar>

					<div v-if="!userWallet || !refundInfo || !refundDetails" class="flex items-center justify-center">
						<ion-spinner name="dots"></ion-spinner>
					</div>

					<div v-if="refundInfo?.topup_age_blocked" class="mt-4 text-center font-sourcesanspro-regular text-white/60">
						{{ t('wallet.refund.topup_age_blocked', { days: refundInfo.online_cutoff_days }) }}
					</div>
					<div v-else-if="userWallet && +userWallet.current_balance <= nonRefundableValue" class="mt-4 text-center font-sourcesanspro-regular text-white/60">
						{{ t('wallet.refund.not_eligible') }}
					</div>
					<div v-if="!refundInfo?.topup_age_blocked && userWallet && refundInfo && refundDetails && +userWallet.current_balance > nonRefundableValue" class="flex flex-col justify-center items-center">
						<div class="mb-2 font-sourcesanspro-regular text-regular text-white/60">
							<div v-if="tenantConfig?.refund_message" v-dompurify-html="tenantConfig?.refund_message" class="flex flex-col mx-[34px] p-4 mb-2 bg-white/10 rounded-lg"></div>
						</div>
						<div class="flex items-start justify-center mb-2">
							<div class="flex flex-col items-center justify-center font-sourcesanspro-regular text-md text-white text-[40px] w-[60px] h-[35px] pt-[14px]">{{ wallet?.currency.prefix }}</div>
							<div class="flex flex-col items-center justify-center mx-1">
								<CurrencyInput :disabled="+userWallet.current_balance <= nonRefundableValue" wrapper-class="flex flex-wrap justify-left item-center border border-solid focus-within:background-white-15" input-class="text-lg" name="amount" placeholder="0,00" step="any" @input-changed="onAmountInputChange" :value="amount" :should-handle-blur="false" />
							</div>
						</div>
						<div v-if="amountErrorMessage" class="flex items-center justify-center mb-4 font-sourcesanspro-regular text-sm font-color-error">
							{{ amountErrorMessage }}
						</div>

						<div class="flex items-start justify-center" v-if="refundInfo?.manual_refund_enabled && refundInfo?.online_amount < +amount">
							<div class="flex flex-col items-center justify-center font-sourcesanspro-regular text-sm text-white text-[40px] w-[60px] pt-[16px]">{{ t('form.iban') }}</div>
							<div class="flex flex-col items-center justify-center mx-1">
								<TextInput wrapper-class="flex w-[295px] flex-wrap justify-left item-center border border-solid focus-within:background-white-15" :value="iban" name="iban" :placeholder="t('form.iban')" :sanitize="true" @input-changed="onInputChange" />
							</div>
						</div>
					</div>
				</form>
				<div v-if="!refundInfo?.topup_age_blocked && userWallet && refundInfo && refundDetails && +userWallet.current_balance > nonRefundableValue" class="flex flex-col items-center justify-center pt-4">
					<ItemsDetails
						border-class="border-y border-y-white/5"
						class="w-full backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4"
						text-class="text-white"
						text-class-two="text-white/70"
						:title="t('queue.information')"
						:items="refundDetails"
						:total-title="t('wallet.refund.received')"
						:total-price="exchange(amount, wallet ? wallet.currency.exchange_rate : 1) - (refundInfo?.cost || 0)"
						:currency="wallet?.currency.prefix" />
					<div v-if="amount > 0 && +(exchange(amount, wallet ? wallet.currency.exchange_rate : 1) - refundInfo.cost || 0).toFixed(2) < 1" class="w-full text-center">
						<span class="mb-4 font-sourcesanspro-regular text-sm font-color-error">
							{{ t('wallet.refund.minimum_refund_amount') }}
						</span>
					</div>
				</div>
			</div>
			<div v-if="!refundInfo?.topup_age_blocked && userWallet && refundInfo && refundDetails && +userWallet.current_balance > nonRefundableValue" class="bottom-0 z-10 bg-transparent px-[34px]">
				<ion-button class="w-full mb-[10px] hover:bg-white hover:rounded-full" expand="full" form="refundsRequestForm" size="large" shape="round" :disabled="!isFormValid || +(exchange(amount, wallet ? wallet.currency.exchange_rate : 1) - refundInfo.cost).toFixed(2) < 1" type="submit">
					<div class="flex w-full items-center justify-between">
						{{ t('wallet.refund.continue') }}
						<ion-icon :icon="arrowForwardOutline" class="text-dark-blue mr-2"></ion-icon>
					</div>
				</ion-button>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
